import { FunctionComponent } from "react";

import { format } from "date-fns";

import { DeepResearchStatusIcon } from "@/components/DeepResearchStatusIcon/DeepResearchStatusIcon";
import { useDeepResearchSocketContext } from "@/pages/Private/pages/ProfileTracker/providers/DeepResearchSocketContext";

import { formatElapsed } from "@/utils/generationTiming";

import { ListPerson } from "../../schema/list";

import type {
	DeepResearchFailure,
	DeepResearchRunView,
} from "@/pages/Private/pages/ProfileTracker/providers/deepResearchRunState";

interface DeepResearchStatusCellProps {
	row: ListPerson;
	onResearchClick?: (row: ListPerson) => void;
}

interface TooltipInput {
	run: DeepResearchRunView | null;
	failure: DeepResearchFailure | null;
	isDone: boolean;
	createdAt: string;
	now: number;
}

/** The cell's tooltip: where the lead's research stands, with its time (PREC-4052). */
export const deepResearchCellTooltip = ({
	run,
	failure,
	isDone,
	createdAt,
	now,
}: TooltipInput): string => {
	if (run) {
		const phase = run.phase === "queued" ? "queued" : "running";

		return `Deep Research / ${phase} · ${formatElapsed(now - run.since)}`;
	}

	if (failure) {
		return failure.kind === "credits"
			? "Deep Research / failed: not enough credits"
			: "Deep Research / failed — open it to try again";
	}

	if (isDone) {
		return `Deep Research / done${createdAt ? ` | ${createdAt}` : ""}`;
	}

	return "Deep Research / not started";
};

export const DeepResearchStatusCell: FunctionComponent<DeepResearchStatusCellProps> = ({
	row,
	onResearchClick,
}) => {
	const { getRun, getFailure } = useDeepResearchSocketContext();

	const deepResearch = row.prospect?.deepResearchResponse;
	const isDone = !!deepResearch?.response && Object.keys(deepResearch.response).length > 0;
	const run = getRun(row.prospect?.id);
	const failure = getFailure(row.prospect?.id);
	const createdAt = deepResearch?.createdAt
		? format(new Date(deepResearch.createdAt), "yyyy-MM-dd HH:mm")
		: "";

	return (
		<DeepResearchStatusIcon
			fitAssessment={deepResearch?.fitAssessment}
			isDone={isDone}
			isFailed={!!failure}
			isQueued={run?.phase === "queued"}
			isRunning={run?.phase === "running"}
			tooltipText={deepResearchCellTooltip({ run, failure, isDone, createdAt, now: Date.now() })}
			onClick={() => onResearchClick?.(row)}
		/>
	);
};
